/**
 * AB#2133 — Pilot support contact (founder-configured) + tickets flag.
 *
 * The real support contact is a founder decision, so the portal never
 * hard-codes an address or number. Values are build-time `REACT_APP_*` vars
 * (CRA inlines them), set on the release-owned Azure Static Web Apps build:
 *
 *   REACT_APP_SUPPORT_EMAIL                  — e.g. `help@example.ae`
 *   REACT_APP_SUPPORT_WHATSAPP               — E.164 digits, optional leading `+`
 *   REACT_APP_FEATURE_SUPPORT_TICKETS_READY  — "true" to re-enable Zammad
 *                                              tickets (default OFF)
 *
 * Unset / blank / invalid → `undefined`, and the Support page shows an honest
 * "being set up" fallback. Any address on the devanza.com domain is rejected:
 * Devanza does not control that domain.
 */

const UNCONTROLLED_EMAIL_DOMAIN = 'devanza.com';
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WHATSAPP_SHAPE = /^\+?(\d{6,15})$/;

export interface SupportContact {
    email?: string;
    /** Digits only (no `+`), ready for `https://wa.me/<digits>`. */
    whatsApp?: string;
}

export function parseSupportEmail(raw: string | undefined | null): string | undefined {
    const value = (raw ?? '').trim();
    if (!value || !EMAIL_SHAPE.test(value)) return undefined;
    const domain = value.slice(value.lastIndexOf('@') + 1).toLowerCase();
    if (domain === UNCONTROLLED_EMAIL_DOMAIN || domain.endsWith(`.${UNCONTROLLED_EMAIL_DOMAIN}`)) {
        return undefined;
    }
    return value;
}

export function parseSupportWhatsApp(raw: string | undefined | null): string | undefined {
    const value = (raw ?? '').trim();
    const match = WHATSAPP_SHAPE.exec(value);
    return match ? match[1] : undefined;
}

export function whatsAppUrl(digits: string): string {
    return `https://wa.me/${digits}`;
}

export function getSupportContact(): SupportContact {
    return {
        email: parseSupportEmail(process.env.REACT_APP_SUPPORT_EMAIL),
        whatsApp: parseSupportWhatsApp(process.env.REACT_APP_SUPPORT_WHATSAPP),
    };
}

/**
 * Support tickets proxy to Zammad, which is OFF for the controlled pilot.
 * While this is false the Support page offers no ticket creation, ticket list
 * or chat — only the configured contact card.
 */
export const isSupportTicketsEnabled = (): boolean =>
    (process.env.REACT_APP_FEATURE_SUPPORT_TICKETS_READY ?? '').toLowerCase() === 'true';
